import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import PositionBrandReportModal from './PositionBrandReportModal';
import { useClient } from '../contexts/ClientContext';
import { UNIT_PRICE_CLIENT_ID } from '../utils/tableHelpers';
import { getProductBrand } from '../utils/positionBrand';
// Multi-tab fix: cliente activo en sessionStorage (per-tab).
import { getStoredSelectedClientRaw } from '../utils/clientStorage';

interface Product {
  id_catalogo: number;
  title: string;
  imagen: string;
  sale_price: string;
  original_price: string;
  discounted_price: string;
  marca?: string;
  Marca?: string;
  sitio: string;
  page?: number;
  orderInPage?: number;
  apariciones?: string | any[] | any;
  cuotas?: string;
  installements?: string;
  precio_cuota?: string;
  installements_value?: string;
  url: string;
  Cat?: string;
  categoria?: string;
  category_toma?: string;
  aparicion_data?: any;
  _expanded_from_apariciones?: boolean;
}

interface CategoryData {
  name: string;
  products: Product[];
  pageRange: { min: number; max: number };
}

interface PriceRange {
  min: number;
  max: number;
  count: number;
  label: string;
  greenCount?: number;
  blueCount?: number;
  redCount?: number;
}

interface PositionGridProps {
  tableName: string;
}

const PositionGrid: React.FC<PositionGridProps> = ({ tableName }) => {
  const { t } = useTranslation();
  const { selectedClient } = useClient();
  const isUnitPriceClient = selectedClient?.clientId === UNIT_PRICE_CLIENT_ID;

  // Restaurar estado desde localStorage para evitar flash al cambiar de pestaña
  const [loading, setLoading] = useState(true); // Iniciar en true para mostrar spinner inmediato
  const [error, setError] = useState<string | null>(null);
  const [sites, setSites] = useState<string[]>([]);
  const [selectedSite, setSelectedSite] = useState<string>(() => {
    try {
      return localStorage.getItem('positionGrid_selectedSite') || '';
    } catch { return ''; }
  });
  const [showSiteDropdown, setShowSiteDropdown] = useState(false);
  const [viewMode, setViewMode] = useState<'product' | 'brand' | 'discount'>(() => {
    try {
      const saved = localStorage.getItem('positionGrid_viewMode');
      return (saved === 'product' || saved === 'brand' || saved === 'discount') ? saved : 'product';
    } catch { return 'product'; }
  });
  const [hoveredProduct, setHoveredProduct] = useState<Product | null>(null);
  const [hoveredPosition, setHoveredPosition] = useState<{ x: number; y: number } | null>(null);
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [groupBy, setGroupBy] = useState<'site' | 'category'>(() => {
    try {
      const saved = localStorage.getItem('positionGrid_groupBy');
      return (saved === 'site' || saved === 'category') ? saved : 'site';
    } catch { return 'site'; }
  });
  const [selectedCategory, setSelectedCategory] = useState<string>(() => {
    try {
      return localStorage.getItem('positionGrid_selectedCategory') || '';
    } catch { return ''; }
  });
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [isInteractingWithDropdown, setIsInteractingWithDropdown] = useState(false);
  const [brands, setBrands] = useState<string[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportCategory, setReportCategory] = useState<CategoryData | null>(null);
  const [referencePrice, setReferencePrice] = useState<string>('');

  // Persistir selecciones en localStorage
  useEffect(() => {
    try {
      if (selectedSite) localStorage.setItem('positionGrid_selectedSite', selectedSite);
    } catch { /* silenciar */ }
  }, [selectedSite]);

  useEffect(() => {
    try {
      if (selectedCategory) localStorage.setItem('positionGrid_selectedCategory', selectedCategory);
    } catch { /* silenciar */ }
  }, [selectedCategory]);

  useEffect(() => {
    try {
      localStorage.setItem('positionGrid_groupBy', groupBy);
    } catch { /* silenciar */ }
  }, [groupBy]);

  useEffect(() => {
    try {
      localStorage.setItem('positionGrid_viewMode', viewMode);
    } catch { /* silenciar */ }
  }, [viewMode]);

  // Función para calcular el color según el precio de referencia
  const calculatePriceColor = (price: number, refPrice: number): string => {
    if (!refPrice) return 'transparent';
    
    // Comparación exacta - sin tolerancia
    if (price === refPrice) {
      return 'rgb(59, 130, 246)'; // Azul - precio exactamente en referencia
    } else if (price < refPrice) {
      return 'rgb(34, 197, 94)'; // Verde - precio por debajo
    } else {
      return 'rgb(239, 68, 68)'; // Rojo - precio por encima
    }
  };

  // Función para parsear el precio
  // Los campos son DECIMAL(10,2), ya están en pesos (44999.00 = $44.999)
  const parsePrice = (price: string | number | null | undefined): number => {
    if (price === null || price === undefined || price === '') return 0;
    
    // Si es número usarlo directamente, si es string parsearlo
    const parsed = typeof price === 'number' ? price : parseFloat(String(price));
    return isNaN(parsed) ? 0 : parsed;
  };

  // Función para calcular los rangos de precios para el histograma
  const calculatePriceRanges = (products: Product[], refPrice?: number): PriceRange[] => {
    if (products.length === 0) return [];
    
    const productsWithPrice = products.filter(p => parsePrice(p.sale_price) > 0);
    if (productsWithPrice.length === 0) return [];
    
    const prices = productsWithPrice.map(p => parsePrice(p.sale_price));
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    
    // Si todos los precios son iguales, devolver un solo rango
    if (min === max) {
      const range: PriceRange = {
        min: min,
        max: max,
        count: productsWithPrice.length,
        label: `$${formatPrice(min)}`
      };
      
      if (refPrice) {
        if (min === refPrice) {
          range.blueCount = productsWithPrice.length;
        } else if (min < refPrice) {
          range.greenCount = productsWithPrice.length;
        } else {
          range.redCount = productsWithPrice.length;
        }
      }
      
      return [range];
    }
    
    // Usar máximo 8 barras para mejor visualización
    const numRanges = Math.min(8, Math.ceil(Math.sqrt(productsWithPrice.length)));
    const rangeSize = (max - min) / numRanges;
    
    const ranges: PriceRange[] = [];
    
    for (let i = 0; i < numRanges; i++) {
      const rangeMin = Math.floor(min + (i * rangeSize));
      const rangeMax = Math.floor(min + ((i + 1) * rangeSize));
      
      const productsInRange = productsWithPrice.filter(p => {
        const price = parsePrice(p.sale_price);
        if (i === numRanges - 1) {
          // Último rango incluye el máximo
          return price >= rangeMin && price <= max;
        }
        return price >= rangeMin && price < rangeMax;
      });
      
      const range: PriceRange = {
        min: rangeMin,
        max: i === numRanges - 1 ? max : rangeMax,
        count: productsInRange.length,
        label: `$${formatPrice(rangeMin)}`
      };
      
      // Calcular distribución de colores si hay precio de referencia
      if (refPrice && productsInRange.length > 0) {
        range.greenCount = 0;
        range.blueCount = 0;
        range.redCount = 0;
        
        productsInRange.forEach(p => {
          const price = parsePrice(p.sale_price);
          if (price === refPrice) {
            range.blueCount!++;
          } else if (price < refPrice) {
            range.greenCount!++;
          } else {
            range.redCount!++;
          }
        });
      }
      
      ranges.push(range);
    }
    
    return ranges;
  };

  // Función para obtener el color de la barra según el precio de referencia
  const getBarColor = (range: PriceRange, refPrice: number): string => {
    if (!refPrice) return '#E5E7EB'; // Gris por defecto
    
    const rangeMid = (range.min + range.max) / 2;
    return calculatePriceColor(rangeMid, refPrice);
  };

  // Cargar sitios disponibles
  useEffect(() => {
    const loadSites = async () => {
      if (!tableName) return;

      try {
        const response = await api.get(`/api/tables/${tableName}/distinct/sitio`);
        const sitesData = response.data.filter((item: any) => item && typeof item === 'string' && item.trim() !== '');
        setSites(sitesData);
        // Solo auto-seleccionar si no hay sitio guardado o el guardado no existe en los datos
        if (sitesData.length > 0 && groupBy === 'site') {
          setSelectedSite(prev => {
            // Si ya hay un sitio seleccionado y existe en los datos, mantenerlo
            if (prev && sitesData.includes(prev)) return prev;
            // Si no, seleccionar el primero
            return sitesData[0];
          });
        }
      } catch (err) {
        console.error('Error loading sites:', err);
      }
    };

    loadSites();
  }, [tableName, groupBy]);

  // Cargar todas las categorías disponibles (priorizando category_toma de apariciones)
  useEffect(() => {
    const loadCategories = async () => {
      if (!tableName) return;

      const selectCategoryIfNeeded = (categoriesData: string[]) => {
        if (categoriesData.length > 0) {
          setSelectedCategory(prev => {
            // Si ya hay una categoría seleccionada y existe en los datos, mantenerla
            if (prev && categoriesData.includes(prev)) return prev;
            return categoriesData[0];
          });
        }
      };

      try {
        let categoriesData: string[] = [];
        if (isUnitPriceClient) {
          // Try CATEGORIA tag first
          try {
            const response = await api.get(`/api/tables/${tableName}/distinct-tag/CATEGORIA`);
            categoriesData = (response.data || [])
              .filter((item: any) => item && typeof item === 'string' && item.trim() !== '')
              .sort((a: string, b: string) => a.localeCompare(b, 'es'));
          } catch (_) { /* fallback below */ }
        }

        // If no tag results or not unit price client, use default approach
        if (categoriesData.length === 0) {
          try {
            const response = await api.get(`/api/tables/${tableName}/distinct-categories`);
            categoriesData = response.data
              .filter((item: any) => item && typeof item === 'string' && item.trim() !== '');
          } catch (_) {
            // Final fallback to Cat column
            const response = await api.get(`/api/tables/${tableName}/distinct/Cat`);
            categoriesData = response.data
              .filter((item: any) => item && typeof item === 'string' && item.trim() !== '')
              .sort((a: string, b: string) => a.localeCompare(b, 'es'));
          }
        }

        setAllCategories(categoriesData);
        selectCategoryIfNeeded(categoriesData);
      } catch (err) {
        console.error('Error loading categories:', err);
      }
    };

    loadCategories();
  }, [tableName, isUnitPriceClient]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.brand-dropdown')) {
        setShowBrandDropdown(false);
      }
      if (!target.closest('.site-dropdown')) {
        setShowSiteDropdown(false);
      }
      if (!target.closest('.category-dropdown')) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Extraer marcas de los productos cargados
  useEffect(() => {
    if (categories.length === 0) {
      // No limpiar brands aquí para mantenerlas mientras se cambia de sitio/categoría
      return;
    }

    // Extraer todas las marcas únicas de los productos en las categorías
    const allProducts = categories.flatMap(cat => cat.products);
    const uniqueBrands = new Set<string>();

    // Resolución de marca POR PRODUCTO (coalesce tag_MARCA → Marca → marca).
    // Antes se usaba un flag global all-or-nothing que descartaba productos con
    // tag_MARCA null aunque tuvieran Marca nativa (bug PT-#39).
    allProducts.forEach(product => {
      const brand = getProductBrand(product);
      if (brand) {
        uniqueBrands.add(brand);
      }
    });

    // Ordenar las marcas alfabéticamente
    const sortedBrands = Array.from(uniqueBrands).sort((a, b) => a.localeCompare(b, 'es'));
    setBrands(sortedBrands);
  }, [categories, isUnitPriceClient]);

  // Organizar productos por categoría
  const organizeByCategory = (products: Product[]): CategoryData[] => {
    const categoryMap = new Map<string, Product[]>();
    
    products.forEach(product => {
      // Use tag_CATEGORIA if available in the data, otherwise use default columns
      const hasTagCat = (product as any).tag_CATEGORIA && (product as any).tag_CATEGORIA.trim() !== '';
      const category = hasTagCat
        ? ((product as any).tag_CATEGORIA)
        : (product.category_toma || product.Cat || product.categoria || 'Sin categoría');
      if (!categoryMap.has(category)) {
        categoryMap.set(category, []);
      }
      categoryMap.get(category)!.push(product);
    });

    const categoriesData: CategoryData[] = [];
    
    categoryMap.forEach((products, categoryName) => {
      const pages = products.map(p => p.page).filter(p => p !== null && p !== undefined) as number[];
      
      // Handle case where pages might be empty
      if (pages.length === 0) {
        categoriesData.push({
          name: categoryName,
          products: products,
          pageRange: {
            min: 0,
            max: 0
          }
        });
      } else {
        categoriesData.push({
          name: categoryName,
          products: products,
          pageRange: {
            min: Math.min(...pages),
            max: Math.max(...pages)
          }
        });
      }
    });

    return categoriesData.sort((a, b) => a.name.localeCompare(b.name, 'es'));
  };

  // Organizar productos por sitio
  const organizeBySite = (products: Product[]): CategoryData[] => {
    const siteMap = new Map<string, Product[]>();
    
    products.forEach(product => {
      const site = product.sitio || 'Sin sitio';
      if (!siteMap.has(site)) {
        siteMap.set(site, []);
      }
      siteMap.get(site)!.push(product);
    });

    const sitesData: CategoryData[] = [];
    
    siteMap.forEach((products, siteName) => {
      const pages = products.map(p => p.page).filter(p => p !== null && p !== undefined) as number[];
      
      // Handle case where pages might be empty
      if (pages.length === 0) {
        sitesData.push({
          name: siteName,
          products: products,
          pageRange: {
            min: 0,
            max: 0
          }
        });
      } else {
        sitesData.push({
          name: siteName,
          products: products,
          pageRange: {
            min: Math.min(...pages),
            max: Math.max(...pages)
          }
        });
      }
    });

    return sitesData.sort((a, b) => a.name.localeCompare(b.name, 'es'));
  };

  // Cargar productos según el modo de agrupación
  useEffect(() => {
    const loadProducts = async () => {
      if (!tableName) return;
      
      
      // No bloquear la carga inicial si no hay selección
      // Solo retornar si explícitamente se está en modo de agrupación y no hay selección
      if (groupBy === 'site' && !selectedSite) {
        setError(null); // Limpiar cualquier error previo
        setCategories([]); // Limpiar categorías
        return;
      }
      if (groupBy === 'category' && !selectedCategory) {
        setError(null); // Limpiar cualquier error previo
        setCategories([]); // Limpiar categorías
        return;
      }
      
      setHoveredProduct(null);
      setHoveredPosition(null);
      
      setLoading(true);
      setError(null);
      setCategories([]); // Limpiar categorías inmediatamente para forzar el spinner
      
      try {
        const params = new URLSearchParams({
          limit: '100000',  // Aumentado para soportar tablas grandes con más de 50,000 productos
          offset: '0',
          orderBy: 'page,orderInPage',
          orderDirection: 'ASC'
        });
        
        if (groupBy === 'site') {
          params.append('sitio', selectedSite);
        } else {
          // The backend will handle filtering by category_toma from apariciones
          params.append('category_toma', selectedCategory);
          // NO filtrar por sitio cuando estamos en modo categoría
          // Queremos ver todos los sitios donde aparece esta categoría
        }
        
        // No filtrar por marca en el backend - se aplica opacity en el frontend
        
        // Use regular endpoint temporarily while we fix the ultra-optimized one
        const response = await api.get(`/api/tables/${tableName}/position-data-optimized?${params}`);
        const data: Product[] = response.data.data;
        
        
        // Debug: ver si los productos tienen category_toma
        if (data.length > 0 && groupBy === 'category') {
        }
        
        // Si no hay datos, limpiar las categorías pero no mostrar error
        if (data.length === 0) {
          setCategories([]);
        } else if (groupBy === 'site') {
          // Cuando seleccionamos un sitio, mostramos las categorías dentro de ese sitio
          const categoriesData = organizeByCategory(data);
          setCategories(categoriesData);
        } else {
          // Cuando seleccionamos una categoría, mostramos los sitios donde aparece esa categoría
          const sitesData = organizeBySite(data);
          setCategories(sitesData);
        }
      } catch (err: any) {
        console.error('🔴 Error loading products in PositionGrid:', err);
        console.error('🔴 Error response:', err.response);
        console.error('🔴 Error message:', err.message);
        
        // Proporcionar un mensaje de error más específico
        if (err.response?.status === 404) {
          setError(t('position.table_not_found'));
        } else if (err.response?.status === 500) {
          setError(t('position.server_error'));
        } else if (err.code === 'ERR_NETWORK') {
          setError(t('position.network_error'));
        } else {
          setError(t('position.error_loading'));
        }
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableName, selectedSite, selectedCategory, groupBy]);

  // Organizar productos por página y orden
  const getProductGridForCategory = (categoryProducts: Product[]) => {
    const grid: { [page: number]: { [order: number]: Product } } = {};
    let maxOrder = 0;
    
    categoryProducts.forEach(product => {
      let page: number;
      let orderInPage: number;
      
      // El backend ya expandió las apariciones, así que cada producto
      // tiene directamente page y orderInPage en el nivel raíz
      if (product.page !== undefined && product.orderInPage !== undefined) {
        page = parseInt(String(product.page), 10);
        orderInPage = parseInt(String(product.orderInPage), 10);
      } else if (product.aparicion_data) {
        // Si tiene aparicion_data, usar esos valores
        page = parseInt(String(product.aparicion_data.page), 10);
        orderInPage = parseInt(String(product.aparicion_data.orderInPage), 10);
      } else if (product.apariciones) {
        // Fallback: si por alguna razón todavía tiene apariciones sin expandir
        try {
          let aparicionesData;
          if (typeof product.apariciones === 'string') {
            // Try to fix Python dict format to valid JSON
            let fixedJson = product.apariciones;
            
            // Check if it looks like Python dict format (has single quotes)
            if (fixedJson.includes("'")) {
              // Convert Python dict to valid JSON
              fixedJson = fixedJson
                .replace(/'/g, '"')  // Replace single quotes with double quotes
                .replace(/(\w+):/g, '"$1":')  // Add quotes to unquoted keys
                .replace(/"{2,}/g, '"')  // Clean up multiple quotes
                .replace(/"("?\w+"?)":/g, (match, key) => {
                  // Clean up over-quoted keys
                  const cleanKey = key.replace(/"/g, '');
                  if (['page', 'orderInPage', 'category_toma'].includes(cleanKey)) {
                    return `"${cleanKey}":`;
                  }
                  return `"${cleanKey}":`;
                });
            }
            
            aparicionesData = JSON.parse(fixedJson);
          } else {
            aparicionesData = product.apariciones;
          }
          
          // Si es un array y tiene elementos, tomar el primero
          // (esto es solo un fallback, normalmente no debería llegar aquí)
          if (Array.isArray(aparicionesData) && aparicionesData.length > 0) {
            page = parseInt(String(aparicionesData[0].page), 10);
            orderInPage = parseInt(String(aparicionesData[0].orderInPage), 10);
          } else {
            // Not an array or empty, skip
            console.warn('Invalid apariciones format:', product.apariciones);
            return;
          }
        } catch (e) {
          console.warn('Error parsing apariciones:', product.apariciones, e);
          return;
        }
      } else {
        // No tiene datos de posición válidos
        console.warn('Product without position data:', product);
        return;
      }
      
      // Skip products without valid position data
      if (isNaN(page) || isNaN(orderInPage)) {
        console.warn('Invalid position data:', { 
          page: product.page, 
          orderInPage: product.orderInPage,
          apariciones: product.apariciones,
          aparicion_data: product.aparicion_data
        });
        return;
      }
      
      if (!grid[page]) {
        grid[page] = {};
      }
      
      // Si ya hay un producto en esta posición, es posible que sea un conflicto
      // En ese caso, mantenemos el primero (o podríamos implementar alguna lógica de prioridad)
      if (!grid[page][orderInPage]) {
        grid[page][orderInPage] = product;
      } else {
      }
      
      maxOrder = Math.max(maxOrder, orderInPage);
    });
    
    return { grid, maxOrder };
  };

  // Formatear precio
  // Los campos son DECIMAL(10,2), ya están en pesos (44999.00 = $44.999)
  const formatPrice = (price: string | number | null | undefined): string => {
    if (price === null || price === undefined || price === '') {
      return '0';
    }
    
    // Si es número usarlo directamente, si es string parsearlo
    const parsed = typeof price === 'number' ? price : parseFloat(String(price));
    
    if (isNaN(parsed)) {
      return '0';
    }
    
    return Math.round(parsed).toLocaleString('es-AR', { 
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    });
  };

  // Calcular descuento
  const calculateDiscount = (product: Product) => {
    // Los campos son DECIMAL(10,2), ya están en pesos
    const cleanPrice = (price: string | number | null | undefined): number => {
      if (!price || price === '') return 0;
      const parsed = typeof price === 'number' ? price : parseFloat(String(price));
      return isNaN(parsed) ? 0 : parsed;
    };
    
    const original = cleanPrice(product.original_price);
    const sale = cleanPrice(product.sale_price);
    const discounted = cleanPrice(product.discounted_price);
    
    if (original === 0) return 0;
    
    if (discounted > 0 && discounted < original) {
      return Math.round(((original - discounted) / original) * 100);
    } else if (sale > 0 && sale < original) {
      return Math.round(((original - sale) / original) * 100);
    }
    return 0;
  };

  const handleMouseEnter = (product: Product, event: React.MouseEvent) => {
    if (isInteractingWithDropdown) return;
    
    setHoveredProduct(product);
    const rect = event.currentTarget.getBoundingClientRect();
    setHoveredPosition({
      x: rect.left + rect.width / 2,
      y: rect.top
    });
  };

  const handleMouseLeave = () => {
    setHoveredProduct(null);
    setHoveredPosition(null);
  };

  // Funciones de navegación horizontal
  const scrollLeft = (containerId: string) => {
    const container = document.getElementById(containerId);
    if (container) {
      container.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = (containerId: string) => {
    const container = document.getElementById(containerId);
    if (container) {
      container.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  // Cerrar dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.site-dropdown')) {
        setShowSiteDropdown(false);
      }
      if (!target.closest('.category-dropdown')) {
        setShowCategoryDropdown(false);
      }
      if (!target.closest('.brand-dropdown')) {
        setShowBrandDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  useEffect(() => {
    if (showSiteDropdown || showCategoryDropdown || showBrandDropdown) {
      setHoveredProduct(null);
      setHoveredPosition(null);
    }
  }, [showSiteDropdown, showCategoryDropdown, showBrandDropdown]);
  
  useEffect(() => {
    setHoveredProduct(null);
    setHoveredPosition(null);
    // Auto-select first option when switching tabs if nothing is selected
    if (groupBy === 'category' && !selectedCategory && allCategories.length > 0) {
      setSelectedCategory(allCategories[0]);
    }
    if (groupBy === 'site' && !selectedSite && sites.length > 0) {
      setSelectedSite(sites[0]);
    }
  }, [groupBy, selectedCategory, selectedSite, allCategories, sites]);

  if (loading) {
    return (
      <div className="p-6">
        {/* Tabs visibles durante la carga */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setGroupBy('site')}
              className={`${
                groupBy === 'site'
                  ? 'border-pink-500 text-pink-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              {t('position.by_site')}
            </button>
            <button
              onClick={() => setGroupBy('category')}
              className={`${
                groupBy === 'category'
                  ? 'border-pink-500 text-pink-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              {t('position.by_category')}
            </button>
          </nav>
        </div>

        {/* Controles skeleton con valores actuales */}
        <div className="mb-6 flex items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {groupBy === 'site' ? t('position.site') : t('position.category')}
            </label>
            <div className="w-64 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-400 flex items-center justify-between">
              <span className="truncate">
                {groupBy === 'site' ? (selectedSite || t('position.loading')) : (selectedCategory || t('position.loading'))}
              </span>
              <ChevronDown className="h-4 w-4 text-gray-300" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('position.view_mode')}</label>
            <div className="w-48 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-400">
              {viewMode === 'product' ? t('position.view_price') : viewMode === 'brand' ? t('position.view_brand') : t('position.view_discount')}
            </div>
          </div>
        </div>

        {/* Spinner de carga */}
        <div className="flex flex-col items-center justify-center h-48">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pink-500 mb-3"></div>
          <div className="text-sm text-gray-500">{t('position.loading')}</div>
        </div>
      </div>
    );
  }

  // Verificar si hay cliente seleccionado
  const selectedClientStr = getStoredSelectedClientRaw();
  if (!selectedClientStr && !error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">
          Por favor, selecciona un cliente para ver las posiciones
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }
  

  // Si no hay categorías, mostrar mensaje apropiado o spinner
  if (categories.length === 0) {
    
    // Si está cargando, mostrar spinner
    if (loading) {
      return (
        <div className="p-6">
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setGroupBy('site')}
                className={`${
                  groupBy === 'site'
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                {t('position.by_site')}
              </button>
              <button
                onClick={() => setGroupBy('category')}
                className={`${
                  groupBy === 'category'
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                {t('position.by_category')}
              </button>
            </nav>
          </div>
          <div className="flex flex-col items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mb-4"></div>
            <div className="text-gray-500">Cargando productos...</div>
          </div>
        </div>
      );
    }
    
    // Si no está cargando y no hay selección
    if ((groupBy === 'site' && !selectedSite) || (groupBy === 'category' && !selectedCategory)) {
      const dropdownOptions = groupBy === 'site' ? sites : allCategories;
      return (
        <div className="p-6">
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setGroupBy('site')}
                className={`${
                  groupBy === 'site'
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                {t('position.by_site')}
              </button>
              <button
                onClick={() => setGroupBy('category')}
                className={`${
                  groupBy === 'category'
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                {t('position.by_category')}
              </button>
            </nav>
          </div>

          {/* Selector */}
          {dropdownOptions.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {groupBy === 'site' ? t('position.site') : t('position.category')}
              </label>
              <select
                className="w-64 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value=""
                onChange={(e) => {
                  if (groupBy === 'site') setSelectedSite(e.target.value);
                  else setSelectedCategory(e.target.value);
                }}
              >
                <option value="" disabled>
                  {groupBy === 'site' ? t('position.select_site_to_view') : t('position.select_category_to_view')}
                </option>
                {dropdownOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-center justify-center h-48">
            <div className="text-gray-500">
              {groupBy === 'site' ? t('position.select_site_to_view') : t('position.select_category_to_view')}
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="p-6">
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setGroupBy('site')}
              className={`${
                groupBy === 'site'
                  ? 'border-pink-500 text-pink-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              {t('position.by_site')}
            </button>
            <button
              onClick={() => setGroupBy('category')}
              className={`${
                groupBy === 'category'
                  ? 'border-pink-500 text-pink-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              {t('position.by_category')}
            </button>
          </nav>
        </div>

        {/* Selector */}
        <div className="mb-6 flex items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {groupBy === 'site' ? t('position.site') : t('position.category')}
            </label>
            <div className="w-64 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-400 flex items-center justify-between">
              <span className="truncate">
                {groupBy === 'site' ? (selectedSite || t('position.loading')) : (selectedCategory || t('position.loading'))}
              </span>
              <ChevronDown className="h-4 w-4 text-gray-300" />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center h-48">
          <div className="text-gray-500">{t('position.no_products_found')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setGroupBy('site');
              setSelectedCategory(''); // Limpiar categoría al cambiar a modo sitio
            }}
            className={`${
              groupBy === 'site'
                ? 'border-pink-500 text-pink-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            {t('position.by_site')}
          </button>
          <button
            onClick={() => {
              setGroupBy('category');
              setSelectedSite(''); // Limpiar sitio al cambiar a modo categoría
            }}
            className={`${
              groupBy === 'category'
                ? 'border-pink-500 text-pink-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            {t('position.by_category')}
          </button>
        </nav>
      </div>

      {/* Controles */}
      <div className="mb-6 flex items-center justify-between relative z-30">
        <div className="flex items-center gap-4">
          {/* Selector de sitio */}
          {groupBy === 'site' && (
            <div className="relative site-dropdown">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('position.site')}</label>
              <button
                onClick={() => {
                  setShowSiteDropdown(!showSiteDropdown);
                  setIsInteractingWithDropdown(true);
                  setHoveredProduct(null);
                  setHoveredPosition(null);
                }}
                className="w-64 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left bg-white flex items-center justify-between"
              >
                <span className="truncate">{selectedSite || t('position.select_site')}</span>
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </button>
            
              {showSiteDropdown && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
                  {sites.map((site) => (
                    <button
                      key={site}
                      onClick={() => {
                        setSelectedSite(site);
                        setShowSiteDropdown(false);
                        setHoveredProduct(null);
                        setHoveredPosition(null);
                        setIsInteractingWithDropdown(true);
                        setTimeout(() => setIsInteractingWithDropdown(false), 500);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                    >
                      {site}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Selector de categoría */}
          {groupBy === 'category' && (
            <div className="relative category-dropdown">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('position.category')}</label>
              <button
                onClick={() => {
                  setShowCategoryDropdown(!showCategoryDropdown);
                  setIsInteractingWithDropdown(true);
                  setHoveredProduct(null);
                  setHoveredPosition(null);
                }}
                className="w-64 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left bg-white flex items-center justify-between"
              >
                <span className="truncate">{selectedCategory || t('position.select_category')}</span>
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </button>
              
              {showCategoryDropdown && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
                  {allCategories.map((category) => (
                    <button
                      key={category}
                      onClick={() => {
                        setSelectedCategory(category);
                        setShowCategoryDropdown(false);
                        setHoveredProduct(null);
                        setHoveredPosition(null);
                        setIsInteractingWithDropdown(true);
                        setTimeout(() => setIsInteractingWithDropdown(false), 500);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                    >
                      {category}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Selector de modo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('position.view_mode')}</label>
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as 'product' | 'brand' | 'discount')}
              className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="product">{t('position.view_price')}</option>
              <option value="brand">{t('position.view_brand')}</option>
              <option value="discount">{t('position.view_discount')}</option>
            </select>
          </div>

          {/* Selector de marca */}
          <div className="relative brand-dropdown">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('position.brands_to_highlight')}</label>
            <button
              onClick={() => {
                setShowBrandDropdown(!showBrandDropdown);
                setIsInteractingWithDropdown(true);
                setHoveredProduct(null);
                setHoveredPosition(null);
              }}
              className="w-64 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left bg-white flex items-center justify-between hover:border-gray-400"
            >
              <span className="truncate">
                {selectedBrands.length > 0 ? 
                  `${selectedBrands.length} ${t('position.selected_brands')}` : 
                  t('position.all_brands')}
              </span>
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </button>
            
            {showBrandDropdown && (
              <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-hidden">
                {/* Search input and clear button */}
                <div className="p-2 border-b border-gray-200">
                  <input
                    type="text"
                    placeholder={t('filters.search_placeholder')}
                    value={brandSearchTerm}
                    onChange={(e) => setBrandSearchTerm(e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
                    onClick={(e) => e.stopPropagation()}
                  />
                  {selectedBrands.length > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedBrands([]);
                      }}
                      className="w-full px-2 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                    >
                      {t('filters.clear_all')}
                    </button>
                  )}
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {brands
                    .filter(brand => brand.toLowerCase().includes(brandSearchTerm.toLowerCase()))
                    .map((brand) => (
                      <label key={brand} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedBrands.includes(brand)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedBrands([...selectedBrands, brand]);
                            } else {
                              setSelectedBrands(selectedBrands.filter(b => b !== brand));
                            }
                            setHoveredProduct(null);
                            setHoveredPosition(null);
                          }}
                          className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="text-sm text-gray-700">{brand}</span>
                      </label>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Precio de referencia */}
        <div className="ml-auto">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('position.reference_price')}</label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">$</span>
            <input
              type="text"
              value={referencePrice}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                setReferencePrice(value);
              }}
              placeholder={t('position.enter_reference_price')}
              className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
        </div>
      </div>

      {/* Grilla */}
      <div className="space-y-8">
        <style dangerouslySetInnerHTML={{
          __html: `
            .table-scroll-container::-webkit-scrollbar {
              height: 12px;
            }
            .table-scroll-container::-webkit-scrollbar-track {
              background: #F7FAFC;
              border-radius: 6px;
            }
            .table-scroll-container::-webkit-scrollbar-thumb {
              background: #CBD5E0;
              border-radius: 6px;
            }
            .table-scroll-container::-webkit-scrollbar-thumb:hover {
              background: #A0AEC0;
            }
          `
        }} />
        
        {categories.map((category, categoryIndex) => {
          const { grid, maxOrder } = getProductGridForCategory(category.products);
          const pages = Object.keys(grid).map(Number).sort((a, b) => a - b);
          const containerId = `table-container-${categoryIndex}`;
          
          if (pages.length === 0) return null;
          
          return (
            <div key={category.name} className="relative border border-gray-200 rounded-lg">
              {/* Header con botones de navegación */}
              <div className="relative">
                <div className="mb-3 border-b border-gray-200 pb-3 px-4 pt-4">
                  <div className="flex items-center">
                    {/* Información del sitio/categoría */}
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-800">{category.name}</h3>
                      <div className="text-xs text-gray-500">
                        {category.products.length} {t('position.products')} • {t('position.pages')} {category.pageRange.min}-{category.pageRange.max}
                      </div>
                    </div>
                    
                    {/* Contenedor centrado para gráfico y estadísticas */}
                    <div className="flex-1 flex justify-center items-center gap-6">
                      {/* Estadísticas de precios */}
                      <div className="text-xs text-gray-600">
                        {(() => {
                          const prices = category.products.map(p => parsePrice(p.sale_price)).filter(p => p > 0);
                          if (prices.length === 0) return null;
                          
                          const min = Math.min(...prices);
                          const max = Math.max(...prices);
                          const sorted = [...prices].sort((a, b) => a - b);
                          const median = sorted.length % 2 === 0
                            ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
                            : sorted[Math.floor(sorted.length / 2)];
                          
                          return (
                            <div className="flex gap-3">
                              <div>
                                <span className="text-gray-400">{t('position.min_price')}</span>
                                <span className="ml-1 font-medium">${formatPrice(min)}</span>
                              </div>
                              <div>
                                <span className="text-gray-400">{t('position.median_price')}</span>
                                <span className="ml-1 font-medium">${formatPrice(median)}</span>
                              </div>
                              <div>
                                <span className="text-gray-400">{t('position.max_price')}</span>
                                <span className="ml-1 font-medium">${formatPrice(max)}</span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      
                      {/* Gráfico de barras */}
                      <div className="flex flex-col items-center">
                        <div className="flex items-end gap-0.5" style={{ height: '36px', minWidth: '80px' }}>
                          {(() => {
                            const refPrice = parsePrice(referencePrice);
                            const ranges = calculatePriceRanges(category.products, refPrice);
                            if (ranges.length === 0) return null;
                            
                            const maxCount = Math.max(...ranges.map(r => r.count));
                            
                            return ranges.map((range, idx) => {
                              const heightPercentage = maxCount > 0 ? (range.count / maxCount) * 100 : 0;
                              
                              // Si hay precio de referencia y distribución de colores
                              if (refPrice && (range.greenCount || range.blueCount || range.redCount)) {
                                const total = range.count;
                                const greenHeight = (range.greenCount || 0) / total * heightPercentage;
                                const blueHeight = (range.blueCount || 0) / total * heightPercentage;
                                const redHeight = (range.redCount || 0) / total * heightPercentage;
                                
                                return (
                                  <div
                                    key={idx}
                                    className="relative group"
                                    style={{ 
                                      width: '8px',
                                      height: '100%',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'flex-end'
                                    }}
                                  >
                                    {/* Segmento rojo (arriba) */}
                                    {redHeight > 0 && (
                                      <div
                                        className="w-full"
                                        style={{
                                          height: `${redHeight}%`,
                                          backgroundColor: 'rgb(239, 68, 68)',
                                          borderTopLeftRadius: '2px',
                                          borderTopRightRadius: '2px'
                                        }}
                                      />
                                    )}
                                    {/* Segmento azul (medio) */}
                                    {blueHeight > 0 && (
                                      <div
                                        className="w-full"
                                        style={{
                                          height: `${blueHeight}%`,
                                          backgroundColor: 'rgb(59, 130, 246)',
                                          borderTopLeftRadius: redHeight === 0 ? '2px' : '0',
                                          borderTopRightRadius: redHeight === 0 ? '2px' : '0'
                                        }}
                                      />
                                    )}
                                    {/* Segmento verde (abajo) */}
                                    {greenHeight > 0 && (
                                      <div
                                        className="w-full"
                                        style={{
                                          height: `${greenHeight}%`,
                                          backgroundColor: 'rgb(34, 197, 94)',
                                          borderTopLeftRadius: redHeight === 0 && blueHeight === 0 ? '2px' : '0',
                                          borderTopRightRadius: redHeight === 0 && blueHeight === 0 ? '2px' : '0'
                                        }}
                                      />
                                    )}
                                    {/* Tooltip mejorado */}
                                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
                                      <div className="bg-gray-900 text-white text-xs rounded px-3 py-2 min-w-[160px]">
                                        <div className="font-semibold mb-1 text-sm">{range.count} {t('position.products')}</div>
                                        <div className="text-xs mb-1 text-gray-300">${formatPrice(range.min)} - ${formatPrice(range.max)}</div>
                                        {(range.greenCount || range.blueCount || range.redCount) && (
                                          <div className="border-t border-gray-700 pt-2 mt-2 space-y-1">
                                            {(range.greenCount || 0) > 0 && (
                                              <div className="flex items-center gap-2">
                                                <span className="w-2 h-2 bg-green-500 rounded-full inline-block flex-shrink-0"></span>
                                                <span className="text-xs">{range.greenCount} {t('position.below_price')}</span>
                                              </div>
                                            )}
                                            {(range.blueCount || 0) > 0 && (
                                              <div className="flex items-center gap-2">
                                                <span className="w-2 h-2 bg-blue-500 rounded-full inline-block flex-shrink-0"></span>
                                                <span className="text-xs">{range.blueCount} {t('position.at_price')}</span>
                                              </div>
                                            )}
                                            {(range.redCount || 0) > 0 && (
                                              <div className="flex items-center gap-2">
                                                <span className="w-2 h-2 bg-red-500 rounded-full inline-block flex-shrink-0"></span>
                                                <span className="text-xs">{range.redCount} {t('position.above_price')}</span>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-900"></div>
                                    </div>
                                  </div>
                                );
                              } else {
                                // Sin precio de referencia, barra gris simple
                                const color = getBarColor(range, refPrice);
                                return (
                                  <div
                                    key={idx}
                                    className="relative group"
                                    style={{ 
                                      width: '8px',
                                      height: '100%',
                                      display: 'flex',
                                      alignItems: 'flex-end'
                                    }}
                                  >
                                    <div
                                      className="w-full transition-all duration-200 rounded-t-sm"
                                      style={{
                                        height: `${heightPercentage}%`,
                                        backgroundColor: color,
                                        minHeight: range.count > 0 ? '2px' : '0'
                                      }}
                                    />
                                    {/* Tooltip simple */}
                                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                                      <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                        <div className="font-semibold">{range.count} {t('position.products')}</div>
                                        <div className="text-xs">${formatPrice(range.min)} - ${formatPrice(range.max)}</div>
                                      </div>
                                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-900"></div>
                                    </div>
                                  </div>
                                );
                              }
                            });
                          })()}
                        </div>
                        <div className="w-full h-px bg-gray-300 mt-1"></div>
                      </div>
                    </div>
                    
                    {/* Espacio para los botones de navegación */}
                    <div className="flex-1"></div>
                  </div>
                </div>
                
                {/* Botones de navegación */}
                <div className="absolute top-2 right-2 flex gap-1">
                  <button
                    onClick={() => {
                      setReportCategory(category);
                      setShowReportModal(true);
                    }}
                    className="bg-white bg-opacity-90 border border-gray-200 rounded p-1.5 hover:bg-gray-50 transition-all"
                    title={t('position.report')}
                  >
                    <FileText className="h-3.5 w-3.5 text-gray-600" />
                  </button>
                  <button
                    onClick={() => scrollLeft(containerId)}
                    className="bg-white bg-opacity-90 border border-gray-200 rounded p-1.5 hover:bg-gray-50 transition-all"
                    title={t('position.navigate_left')}
                  >
                    <ChevronLeft className="h-3.5 w-3.5 text-gray-600" />
                  </button>
                  <button
                    onClick={() => scrollRight(containerId)}
                    className="bg-white bg-opacity-90 border border-gray-200 rounded p-1.5 hover:bg-gray-50 transition-all"
                    title={t('position.navigate_right')}
                  >
                    <ChevronRight className="h-3.5 w-3.5 text-gray-600" />
                  </button>
                </div>
              </div>
              
              {/* Contenedor scrolleable */}
              <div 
                id={containerId}
                className="overflow-x-auto table-scroll-container px-4 pb-4"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#CBD5E0 #F7FAFC'
                }}
              >
                <table 
                  className="border-collapse mb-4" 
                  style={{ 
                    tableLayout: 'fixed',
                    minWidth: `${Math.max(600, (maxOrder + 1) * 75)}px`,
                    width: `${(maxOrder + 1) * 75}px`
                  }}
                >
                  <thead>
                    <tr>
                      <th className="border border-gray-300 bg-gray-100 px-2 py-1 text-xs font-medium" style={{ width: '75px', minWidth: '75px' }}>
                        {t('position.page')}
                      </th>
                      {Array.from({ length: maxOrder }, (_, i) => (
                        <th key={i + 1} className="border border-gray-300 bg-gray-100 px-1 py-1 text-xs font-medium text-center" style={{ width: '75px', minWidth: '75px' }}>
                          {i + 1}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pages.map(page => (
                      <tr key={page}>
                        <td className="border border-gray-300 bg-gray-50 px-2 py-1 text-xs font-medium text-center" style={{ width: '75px', minWidth: '75px' }}>
                          {page}
                        </td>
                        {Array.from({ length: maxOrder }, (_, i) => {
                          const order = i + 1;
                          const product = grid[page]?.[order];
                          
                          if (!product) {
                            return (
                              <td key={order} className="border border-gray-300 p-1" style={{ width: '75px', minWidth: '75px', height: '90px' }}>
                                &nbsp;
                              </td>
                            );
                          }
                          
                          const discount = calculateDiscount(product);
                          const hasDiscount = discount > 0;
                          
                          const productPrice = parsePrice(product.sale_price);
                          const refPrice = parsePrice(referencePrice);
                          const borderColor = referencePrice ? calculatePriceColor(productPrice, refPrice) : 'transparent';
                          
                          // Resolución de marca POR PRODUCTO (coalesce tag_MARCA → Marca → marca).
                          const productBrand = getProductBrand(product);

                          const isHighlighted = selectedBrands.length === 0 || selectedBrands.includes(productBrand);
                          
                          // Debug para el primer producto de cada categoría
                          if (order === 1 && selectedBrands.length > 0) {
                          }
                          
                          return (
                            <td 
                              key={order} 
                              className="border border-gray-300 p-1 relative bg-white cursor-pointer"
                              style={{ 
                                width: '75px', 
                                minWidth: '75px', 
                                height: '90px'
                              }}
                              onMouseEnter={(e) => handleMouseEnter(product, e)}
                              onMouseLeave={handleMouseLeave}
                              onClick={() => window.open(product.url, '_blank')}
                            >
                              {/* Borde interno para precio de referencia */}
                              {referencePrice && borderColor !== 'transparent' && (
                                <div 
                                  className="absolute pointer-events-none z-10"
                                  style={{
                                    top: '1px',
                                    left: '1px',
                                    right: '1px',
                                    bottom: '1px',
                                    border: `2px solid ${borderColor}`,
                                    borderRadius: '2px'
                                  }}
                                />
                              )}
                              
                              {/* Borde interno para descuentos (cuando hay marcas seleccionadas) */}
                              {hasDiscount && selectedBrands.length > 0 && !referencePrice && (
                                <div 
                                  className="absolute pointer-events-none z-10"
                                  style={{
                                    top: '1px',
                                    left: '1px',
                                    right: '1px',
                                    bottom: '1px',
                                    border: isHighlighted ? '2px solid rgb(239, 68, 68)' : '2px solid rgba(239, 68, 68, 0.3)',
                                    borderRadius: '2px'
                                  }}
                                />
                              )}
                              
                              {/* Borde interno para descuentos (cuando NO hay marcas seleccionadas ni precio de referencia) */}
                              {hasDiscount && selectedBrands.length === 0 && !referencePrice && (
                                <div 
                                  className="absolute pointer-events-none z-10"
                                  style={{
                                    top: '1px',
                                    left: '1px',
                                    right: '1px',
                                    bottom: '1px',
                                    border: '2px solid rgb(239, 68, 68)',
                                    borderRadius: '2px'
                                  }}
                                />
                              )}
                              <div className={`flex flex-col items-center ${!isHighlighted && selectedBrands.length > 0 ? 'opacity-10' : ''}`}>
                                <div className="relative w-14 h-14 mb-1">
                                  {product.imagen && product.imagen.trim() !== '' ? (
                                    <>
                                      <img
                                        src={product.imagen}
                                        alt={product.title}
                                        loading="lazy"
                                        className="w-full h-full object-contain"
                                        onError={(e) => {
                                          e.currentTarget.style.display = 'none';
                                          const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                                          if (placeholder) placeholder.style.display = 'flex';
                                        }}
                                      />
                                      <div className="hidden absolute inset-0 items-center justify-center">
                                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M23 19C23 20.1046 22.1046 21 21 21H3C1.89543 21 1 20.1046 1 19V8C1 6.89543 1.89543 6 3 6H7L9 3H15L17 6H21C22.1046 6 23 6.89543 23 8V19Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <circle cx="12" cy="13" r="4" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                          </svg>
                                        </div>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="flex absolute inset-0 items-center justify-center">
                                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M23 19C23 20.1046 22.1046 21 21 21H3C1.89543 21 1 20.1046 1 19V8C1 6.89543 1.89543 6 3 6H7L9 3H15L17 6H21C22.1046 6 23 6.89543 23 8V19Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                          <circle cx="12" cy="13" r="4" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                {viewMode === 'product' && (
                                  <span 
                                    className="text-xs font-medium"
                                    style={{
                                      color: referencePrice && borderColor !== 'transparent' ? borderColor : undefined
                                    }}
                                  >
                                    ${formatPrice(product.sale_price)}
                                  </span>
                                )}
                                
                                {viewMode === 'brand' && (
                                  <span className="text-xs font-medium text-center">
                                    {product.marca || product.Marca || '-'}
                                  </span>
                                )}
                                
                                {viewMode === 'discount' && (
                                  <span className={`text-xs font-bold ${hasDiscount ? 'text-red-600' : 'text-gray-400'}`}>
                                    {hasDiscount ? `${discount}%` : '-'}
                                  </span>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tooltip */}
      {hoveredProduct && hoveredPosition && (
        <div 
          className="fixed z-50 pointer-events-none"
          style={{
            left: Math.min(hoveredPosition.x, window.innerWidth - 200),
            top: Math.max(hoveredPosition.y - 250, 10),
            transform: hoveredPosition.x > window.innerWidth - 200 ? 'translateX(-100%)' : 'translateX(-50%)'
          }}
        >
          {(() => {
            const tooltipPrice = parsePrice(hoveredProduct.sale_price);
            const tooltipRefPrice = parsePrice(referencePrice);
            const tooltipColor = referencePrice ? calculatePriceColor(tooltipPrice, tooltipRefPrice) : undefined;
            
            return (
              <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-3 w-48">
                <div className="relative w-full h-32 mb-2">
              {hoveredProduct.imagen && hoveredProduct.imagen.trim() !== '' ? (
                <>
                  <img
                    src={hoveredProduct.imagen}
                    alt={hoveredProduct.title}
                    loading="lazy"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                      if (placeholder) placeholder.style.display = 'flex';
                    }}
                  />
                  <div className="hidden absolute inset-0 items-center justify-center">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M23 19C23 20.1046 22.1046 21 21 21H3C1.89543 21 1 20.1046 1 19V8C1 6.89543 1.89543 6 3 6H7L9 3H15L17 6H21C22.1046 6 23 6.89543 23 8V19Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="13" r="4" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex absolute inset-0 items-center justify-center">
                  <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M23 19C23 20.1046 22.1046 21 21 21H3C1.89543 21 1 20.1046 1 19V8C1 6.89543 1.89543 6 3 6H7L9 3H15L17 6H21C22.1046 6 23 6.89543 23 8V19Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="13" r="4" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              )}
            </div>
            <h3 className="text-xs font-medium text-gray-900 line-clamp-2 mb-1">
              {hoveredProduct.title}
            </h3>
            <div className="space-y-1">
              <p className="text-xs text-gray-600">
                {hoveredProduct.marca || hoveredProduct.Marca}
              </p>
              <div className="flex items-center gap-2">
                {hoveredProduct.original_price && hoveredProduct.original_price !== hoveredProduct.sale_price && (
                  <span className="text-xs text-gray-400" style={{ textDecoration: 'line-through', textDecorationStyle: 'dotted' }}>
                    ${formatPrice(hoveredProduct.original_price)}
                  </span>
                )}
                <span 
                  className="text-sm font-bold"
                  style={{
                    color: tooltipColor && tooltipColor !== 'transparent' ? tooltipColor : '#111827'
                  }}
                >
                  ${formatPrice(hoveredProduct.sale_price)}
                </span>
                {calculateDiscount(hoveredProduct) > 0 && (
                  <span className="text-xs font-bold text-red-600">
                    -{calculateDiscount(hoveredProduct)}%
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500">
                {t('position.page_position', { page: hoveredProduct.page, position: hoveredProduct.orderInPage })}
              </p>
              {/* Mostrar todas las apariciones si las hay */}
              {hoveredProduct.apariciones && (() => {
                try {
                  let aparicionesData;
                  if (typeof hoveredProduct.apariciones === 'string') {
                    aparicionesData = JSON.parse(hoveredProduct.apariciones);
                  } else {
                    aparicionesData = hoveredProduct.apariciones;
                  }
                  
                  if (Array.isArray(aparicionesData) && aparicionesData.length > 1) {
                    return (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-700 mb-1">
                          {t('position.all_positions', { count: aparicionesData.length })}:
                        </p>
                        <div className="space-y-0.5 max-h-20 overflow-y-auto">
                          {aparicionesData.map((ap, idx) => (
                            <p key={idx} className="text-xs text-gray-500">
                              • {ap.category_toma}: Pág {ap.page}, Pos {ap.orderInPage}
                            </p>
                          ))}
                        </div>
                      </div>
                    );
                  }
                } catch (e) {
                  return null;
                }
                return null;
              })()}
            </div>
          </div>
            );
          })()}
        </div>
      )}

      {/* Modal de reporte de marcas */}
      {showReportModal && reportCategory && (
        <PositionBrandReportModal
          isOpen={showReportModal}
          onClose={() => {
            setShowReportModal(false);
            setReportCategory(null);
          }}
          category={reportCategory}
          selectedBrands={selectedBrands}
          selectedSite={selectedSite}
          groupBy={groupBy}
        />
      )}
    </div>
  );
};

export default PositionGrid;